import { useState } from "react";
import { useAllFeatureFlags } from "../../feature-flags";
import { announcements } from "../data/announcements";
import type { Announcement } from "../types";

interface ReleaseBannerResult {
  announcement: Announcement;
  dismiss: () => void;
}

function isWithinVisibilityWindow(announcement: Announcement): boolean {
  const now = new Date();
  const start = new Date(announcement.releaseDate + "T00:00:00");
  const end = new Date(start);
  end.setDate(end.getDate() + announcement.expiryDays);
  return now >= start && now <= end;
}

function isDismissed(id: string): boolean {
  return localStorage.getItem(`announcement_dismissed_${id}`) === "true";
}

function isFeatureFlagEnabled(
  announcement: Announcement,
  flags: Record<string, unknown>,
): boolean {
  if (!announcement.featureFlag) return true;
  const value = flags[announcement.featureFlag.flag];
  return typeof value === "boolean" ? value : announcement.featureFlag.default;
}

export function useReleaseBanner(): ReleaseBannerResult | null {
  const [, rerender] = useState(0);
  const flags = useAllFeatureFlags();

  const announcement = announcements.find(
    (a) =>
      isWithinVisibilityWindow(a) &&
      !isDismissed(a.id) &&
      isFeatureFlagEnabled(a, flags),
  ) ?? null;

  if (!announcement) {
    return null;
  }

  return {
    announcement,
    dismiss: () => {
      localStorage.setItem(
        `announcement_dismissed_${announcement.id}`,
        "true",
      );
      rerender((n) => n + 1);
    },
  };
}
